/**
 * The catalogue, fetched once per session and shared.
 *
 * Library, store and detail all want the same thing, and a player moving
 * between them should not re-fetch it each time. Kept out of the session
 * context deliberately: a guest joining a friend's table never needs it, and
 * fetching a shop for somebody who only wants to play a hand is waste.
 */

import { useCallback, useEffect, useState } from 'react';
import { catalog as fetchCatalog } from './net/session.js';
import type { Catalog } from './net/session.js';
import { useSession } from './session-context.js';

let cached: Catalog | null = null;

/** After a purchase the cache is a lie. */
function invalidateCatalog(): void {
  cached = null;
}

export function useCatalog(): {
  catalog: Catalog | null;
  error: string | null;
  retry: () => void;
  refresh: () => Promise<void>;
} {
  const { session } = useSession();
  const [value, setValue] = useState<Catalog | null>(cached);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const load = useCallback(async () => {
    if (!session) return;
    try {
      const fresh = await fetchCatalog(session);
      cached = fresh;
      setValue(fresh);
      setError(null);
    } catch {
      setError('Could not load your games. Check the server is running.');
    }
  }, [session]);

  useEffect(() => {
    if (cached && attempt === 0) {
      setValue(cached);
      return;
    }
    void load();
  }, [load, attempt]);

  const refresh = useCallback(async () => {
    invalidateCatalog();
    await load();
  }, [load]);

  return {
    catalog: value,
    error,
    retry: () => setAttempt((n) => n + 1),
    refresh,
  };
}
